
import { supabase } from '@/integrations/supabase/client';
import { Payment } from '@/services/types';
import { toast } from '@/components/ui/sonner';

/**
 * Создание авторизационного платежа для подписки
 */
export async function createAuthorizationPayment(
  subscription_id: string,
  courier_id: string,
  amount: number = 1,
  receipt_url?: string
): Promise<{ success: boolean; payment: Payment | null; error: string | null }> {
  try {
    console.log(`Создание авторизационного платежа для подписки ${subscription_id}`);
    
    // Получаем данные курьера для отображения имени
    const { data: courierData, error: courierError } = await supabase
      .from('couriers')
      .select('name')
      .eq('id', courier_id)
      .single();

    if (courierError) {
      console.error('Ошибка получения данных курьера:', courierError);
      return { success: false, payment: null, error: courierError.message };
    }

    // Получаем данные подписки
    const { data: subscriptionData, error: subscriptionError } = await supabase
      .from('subscriptions')
      .select('rental_id')
      .eq('id', subscription_id)
      .single();

    if (subscriptionError) {
      console.error('Ошибка получения данных подписки:', subscriptionError);
      return { success: false, payment: null, error: subscriptionError.message };
    }

    const rental_id = subscriptionData.rental_id;

    // Получаем human_number аренды, если доступен
    let rental_human_number = null;
    if (rental_id) {
      const { data: rentalData } = await supabase
        .from('rentals')
        .select('human_number')
        .eq('id', rental_id)
        .single();
      
      if (rentalData) {
        rental_human_number = rentalData.human_number;
      }
    }

    // Формируем человекочитаемый номер платежа
    const now = new Date();
    const human_number = `AUTH-${now.getFullYear()}${(now.getMonth() + 1).toString().padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`;

    // Создаем запись в таблице payments
    const paymentData = {
      courier_id,
      amount,
      rental_id: rental_id || null,
      payment_date: new Date().toISOString(),
      status: 'auth',
      payment_method: 'card',
      human_number,
      receipt_url: receipt_url || null,
      subscription_id
    };

    const { data: payment, error: paymentError } = await supabase
      .from('payments')
      .insert([paymentData])
      .select()
      .single();

    if (paymentError) {
      console.error('Ошибка создания авторизационного платежа:', paymentError);
      toast.error(`Не удалось создать авторизационный платеж: ${paymentError.message}`);
      return { success: false, payment: null, error: paymentError.message };
    }

    // Преобразуем данные в формат Payment
    const formattedPayment: Payment = {
      id: payment.id,
      rentalId: payment.rental_id || '',
      rentalHumanNumber: rental_human_number,
      courierId: payment.courier_id,
      courierName: courierData.name || 'Неизвестный курьер',
      amount: payment.amount,
      date: payment.payment_date,
      status: 'auth',
      method: 'card',
      human_number: payment.human_number,
      receiptUrl: payment.receipt_url,
      subscription_id: payment.subscription_id
    };

    toast.success('Авторизационный платеж создан успешно');
    return { success: true, payment: formattedPayment, error: null };
  } catch (err: any) {
    console.error('Непредвиденная ошибка при создании авторизационного платежа:', err);
    toast.error('Произошла ошибка при создании авторизационного платежа');
    return { success: false, payment: null, error: err.message };
  }
}

/**
 * Активация подписки после успешной авторизации платежа
 * @param subscription_id ID подписки для активации
 * @param payment_id ID платежа (опционально, если не указан, будет создан новый)
 */
export async function activateSubscriptionAfterAuth(
  subscription_id: string,
  payment_id?: string
): Promise<{ success: boolean; error: string | null }> {
  try {
    console.log(`Активация подписки ${subscription_id}${payment_id ? ` после авторизации платежа ${payment_id}` : ''}`);
    
    // Если payment_id не указан, создаем новый авторизационный платеж
    if (!payment_id) {
      console.log('ID платежа не указан, создаем новый платеж для подписки');
      
      // Получаем данные подписки для создания платежа
      const { data: subscriptionData, error: subscriptionError } = await supabase
        .from('subscriptions')
        .select(`
          id,
          rental_id,
          amount,
          rentals:rentals!rental_id (
            courier_id
          )
        `)
        .eq('id', subscription_id)
        .single();
      
      if (subscriptionError) {
        console.error('Ошибка получения данных подписки:', subscriptionError);
        return { success: false, error: `Не удалось получить данные подписки: ${subscriptionError.message}` };
      }
      
      // Правильно обрабатываем связанные данные
      const rentalData = Array.isArray(subscriptionData.rentals) ? subscriptionData.rentals[0] : subscriptionData.rentals;
      
      if (!rentalData?.courier_id) {
        return { success: false, error: 'ID курьера не найден для данной подписки' };
      }
      
      // Создаем авторизационный платеж с минимальной суммой
      const { success, payment, error } = await createAuthorizationPayment(
        subscription_id,
        String(rentalData.courier_id),
        1 // Минимальная сумма для авторизации
      );
      
      if (!success || !payment) {
        return { success: false, error: `Не удалось создать авторизационный платеж: ${error}` };
      }
      
      // Используем ID нового платежа
      payment_id = payment.id;
    }
    
    // Обновляем статус подписки
    const { error: subscriptionError } = await supabase
      .from('subscriptions')
      .update({ status: 'active' })
      .eq('id', subscription_id);

    if (subscriptionError) {
      console.error('Ошибка активации подписки:', subscriptionError);
      return { success: false, error: subscriptionError.message };
    }

    // Обновляем статус платежа, если он указан
    if (payment_id) {
      const { error: paymentError } = await supabase
        .from('payments')
        .update({ status: 'completed' })
        .eq('id', payment_id);

      if (paymentError) {
        console.error('Ошибка обновления статуса платежа:', paymentError);
        // Не считаем это критической ошибкой, продолжаем
      }
    }

    toast.success('Подписка успешно активирована');
    return { success: true, error: null };
  } catch (err: any) {
    console.error('Непредвиденная ошибка при активации подписки:', err);
    return { success: false, error: err.message };
  }
}

export const getCourierIdFromSubscription = async (subscriptionId: string): Promise<string | null> => {
  try {
    const { data, error } = await supabase
      .from('subscriptions')
      .select(`
        rental_id,
        rentals!rental_id (
          courier_id
        )
      `)
      .eq('id', subscriptionId)
      .single();

    if (error || !data) {
      console.error('Ошибка при получении courier_id:', error);
      return null;
    }

    // Правильная обработка данных связи
    const rentalData = data.rentals;
    if (Array.isArray(rentalData) && rentalData.length > 0) {
      return String(rentalData[0]?.courier_id) || null;
    } else if (rentalData && typeof rentalData === 'object' && 'courier_id' in rentalData) {
      return String(rentalData.courier_id) || null;
    }

    return null;
  } catch (error) {
    console.error('Ошибка при получении courier_id из подписки:', error);
    return null;
  }
};
